import _ from "lodash";
import React, { useMemo, useState, useCallback } from "react";
import { EmptyState, Modal, Tabs } from "@toddle-edu/ds-web";
import classNames from "classnames";
import {
  AllCompletedIllustration,
  ChatIllustration,
} from "@toddle-edu/ds-theme";
import { ChevronUpOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { prepareLoadMoreFeed, onEnterOrSpaceKeyDown } from "Utils";
import { useHandleScroll } from "Utils/CustomHooks";
import { compose, graphql } from "lib/apolloClient";
import { getOrganizationId } from "modules/CommonSelectors";
import {
  getMessageReadReceiptCountQuery,
  getMessageReadReceiptUserQuery,
} from "MessagingV2/modules/MessagingQuery";

import classes from "./ReadReceiptModal.scss";
import RecipientsList from "./components/RecipientsList";

const COHORTS = {
  STAFF: "STAFF",
  STUDENT: "STUDENT",
  FAMILY_MEMBER: "FAMILY_MEMBER",
};

const COHORT_ORDER = [COHORTS.STAFF, COHORTS.STUDENT, COHORTS.FAMILY_MEMBER];

const COHORT_CONFIG = {
  [COHORTS.STAFF]: {
    key: "staff",
    locales: "common:staff_member_other",
  },
  [COHORTS.STUDENT]: {
    key: "student",
    locales: "student_other",
  },
  [COHORTS.FAMILY_MEMBER]: {
    key: "parent",
    locales: "common:family_member_other",
  },
};

const modalBodyStyle = {
  padding: "0px",
  overflow: "hidden",
};

const Accordion = ({ title, isOpen, onToggle }) => {
  const handleKeyDown = e => {
    onEnterOrSpaceKeyDown(e, onToggle);
  };

  return (
    <div
      className={classes.accordion}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="text-label-3">{title}</div>

      <div
        className={classNames(classes.accordionIcon, {
          [classes.accordionClose]: !isOpen,
        })}
      >
        <ChevronUpOutlined />
      </div>
    </div>
  );
};

const CohortList = props => {
  const { t } = useI18n(["common"]);

  const [collapsedCohorts, setCollapsedCohorts] = useState({
    [COHORTS.STAFF]: false,
    [COHORTS.STUDENT]: false,
    [COHORTS.FAMILY_MEMBER]: false,
  });

  const toggleCohort = cohort => {
    setCollapsedCohorts(prev => ({
      ...prev,
      [cohort]: !prev[cohort],
    }));
  };

  const getFirstOpenCohortWithNextPage = useCallback(() => {
    const firstCohort = _.find(COHORT_ORDER, cohort => {
      const isOpen = !collapsedCohorts[cohort];
      if (!isOpen) return false;

      const cohortKey = _.get(COHORT_CONFIG, [cohort, "key"]);
      const cohortData = _.get(props, cohortKey);
      const hasNextPage = _.get(cohortData, "hasNextPage", false);

      return hasNextPage;
    });

    if (!firstCohort) return null;

    const cohortKey = _.get(COHORT_CONFIG, [firstCohort, "key"]);
    const cohortData = _.get(props, cohortKey);

    return { cohort: firstCohort, cohortKey, cohortData };
  }, [collapsedCohorts, props]);

  const loadMoreUsers = useCallback(() => {
    const cohortInfo = getFirstOpenCohortWithNextPage();
    if (!cohortInfo) return;

    const { cohortData } = cohortInfo;
    const onLoadMore = _.get(cohortData, "onLoadMore");

    if (onLoadMore) {
      onLoadMore();
    }
  }, [getFirstOpenCohortWithNextPage]);

  const hasNextPage = !!getFirstOpenCohortWithNextPage();

  const isFetchingMore = _.some(COHORT_ORDER, cohort => {
    const cohortKey = _.get(COHORT_CONFIG, [cohort, "key"]);
    const cohortData = _.get(props, cohortKey);
    return _.get(cohortData, "isLoading", false);
  });

  useHandleScroll({
    networkStatus: isFetchingMore ? 3 : 1,
    loadMoreFeed: loadMoreUsers,
    hasNextPage,
    getScrollRef: () => props.scrollRef?.current,
    throttleTime: 300,
    hookDependencies: [loadMoreUsers, hasNextPage],
  });

  const shouldShowCohort = useCallback(
    cohort => {
      const cohortIndex = _.indexOf(COHORT_ORDER, cohort);

      const hasPrevCohortWithNextPage = _.some(
        _.slice(COHORT_ORDER, 0, cohortIndex),
        prevCohort => {
          const isOpen = !collapsedCohorts[prevCohort];
          if (!isOpen) return false;

          const cohortKey = _.get(COHORT_CONFIG, [prevCohort, "key"]);
          const cohortData = _.get(props, cohortKey);
          return _.get(cohortData, "hasNextPage", false);
        }
      );

      return !hasPrevCohortWithNextPage;
    },
    [collapsedCohorts, props]
  );
  return (
    <div className={classes.cohortContainer}>
      {_.map(COHORT_ORDER, cohort => {
        const cohortKey = _.get(COHORT_CONFIG, [cohort, "key"]);
        const cohortLocales = _.get(COHORT_CONFIG, [cohort, "locales"]);
        const cohortData = _.get(props, cohortKey);
        const usersEdges = _.get(cohortData, "usersEdges", []);
        const users = _.map(usersEdges, "node");

        if (_.isEmpty(users)) {
          return null;
        }

        if (!shouldShowCohort(cohort)) {
          return null;
        }

        const isOpen = !collapsedCohorts[cohort];

        return (
          <div className={classes.cohortList} key={cohort}>
            <Accordion
              title={t(cohortLocales)}
              isOpen={isOpen}
              onToggle={() => toggleCohort(cohort)}
            />
            {isOpen && <RecipientsList users={users} />}
          </div>
        );
      })}
      {isFetchingMore && (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      )}
    </div>
  );
};

const getReadReceiptUserQueryEnhancer = ({ cohort }) => {
  const cohortKey = _.get(COHORT_CONFIG, [cohort, "key"]);

  return graphql(getMessageReadReceiptUserQuery, {
    name: "getMessageReadReceiptUser",
    options: ({ messageId, tab }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: messageId,
        organizationId: getOrganizationId(),
        input: {
          messageState: tab === READ_RECEIPT_TAB.READ ? "READ" : "UNREAD",
          userType: [cohortKey],
          first: 20,
        },
      },
    }),
    props: ({
      getMessageReadReceiptUser,
      ownProps: { isLoading = false, isData = true },
    }) => {
      const { node } = getMessageReadReceiptUser || {};

      const queryVariables = _.get(getMessageReadReceiptUser, "variables", {});
      const edges = _.get(node, "readReceipt.edges", []);
      const isLoadingMore = _.eq(getMessageReadReceiptUser.networkStatus, 3);

      const cohortData = {
        usersEdges: edges,
        hasNextPage: _.get(node, "readReceipt.pageInfo.hasNextPage"),
        isLoading: isLoadingMore,
        onLoadMore: prepareLoadMoreFeed({
          query: getMessageReadReceiptUserQuery,
          variables: {
            ...queryVariables,
            input: {
              ...queryVariables.input,
              before: _.get(node, "readReceipt.pageInfo.endCursor"),
            },
          },
          queryData: getMessageReadReceiptUser,
          key: "readReceipt",
        }),
      };

      return {
        isLoading:
          _.includes([1, 2], getMessageReadReceiptUser.networkStatus) ||
          isLoading,
        isData: !_.isEmpty(node) && isData,
        [cohortKey]: cohortData,
      };
    },
  });
};

const READ_RECEIPT_TAB = {
  READ: "READ",
  UNREAD: "UNREAD",
};

const UsersListWithData = compose(
  getReadReceiptUserQueryEnhancer({
    cohort: COHORTS.STAFF,
  }),
  getReadReceiptUserQueryEnhancer({
    cohort: COHORTS.STUDENT,
  }),
  getReadReceiptUserQueryEnhancer({
    cohort: COHORTS.FAMILY_MEMBER,
  }),
  withLoader
)(CohortList);

const getModalTabs = ({ t, readReceiptCount }) => {
  const readCount = _.get(readReceiptCount, "readCount", 0);
  const unreadCount = _.get(readReceiptCount, "unreadCount", 0);

  return [
    {
      label: t("common:read_by"),
      value: READ_RECEIPT_TAB.READ,
      suffix: <div>{`(${readCount})`}</div>,
    },
    {
      label: t("common:pending"),
      value: READ_RECEIPT_TAB.UNREAD,
      suffix: <div>{`(${unreadCount})`}</div>,
    },
  ];
};

const EmptyReadReceiptScreen = props => {
  const { tab } = props;
  const { t } = useI18n(["common"]);

  return (
    <EmptyState
      illustration={
        _.isEqual(tab, READ_RECEIPT_TAB.READ)
          ? ChatIllustration
          : AllCompletedIllustration
      }
      title={
        _.isEqual(tab, READ_RECEIPT_TAB.READ)
          ? t("common:no_one_has_read_this_message")
          : t("common:everyone_caught_up")
      }
      subtitle={
        _.isEqual(tab, READ_RECEIPT_TAB.READ)
          ? t("common:message_read_by_all_members_of_channel")
          : t("common:members_listed_here_once_view_the_message")
      }
      size="small"
      dsVersion="2.0"
    />
  );
};

const RecipientsModal = props => {
  const { readReceiptCount } = props;
  const listContainerRef = React.useRef(null);

  const [tab, setTab] = useState(READ_RECEIPT_TAB.READ);

  const { t } = useI18n(["common"]);

  const tabOptions = useMemo(() => getModalTabs({ readReceiptCount, t }), [
    readReceiptCount,
    t,
  ]);

  const currentTabCount =
    tab === READ_RECEIPT_TAB.READ
      ? _.get(readReceiptCount, "readCount")
      : _.get(readReceiptCount, "unreadCount");

  const isCurrentTabEmpty = _.eq(currentTabCount, 0);

  return (
    <div className={classes.listAndTabContainer}>
      <div className={classes.tabContainer}>
        <Tabs
          dsVersion="2.0"
          options={tabOptions}
          onChange={setTab}
          value={tab}
        />
      </div>
      <div className={classes.listContainer} ref={listContainerRef}>
        {isCurrentTabEmpty ? (
          <EmptyReadReceiptScreen tab={tab} />
        ) : (
          <UsersListWithData
            tab={tab}
            messageId={props.messageId}
            scrollRef={listContainerRef}
          />
        )}
      </div>
    </div>
  );
};

const RecipientsModalWithData = compose(
  graphql(getMessageReadReceiptCountQuery, {
    name: "getMessageReadReceiptCount",
    options: ({ messageId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: messageId,
        organizationId: getOrganizationId(),
        readInput: { messageState: "READ" },
        unreadInput: { messageState: "UNREAD" },
      },
    }),
    props: ({ getMessageReadReceiptCount }) => {
      const { node } = getMessageReadReceiptCount || {};

      const readReceiptCount = _.get(node, "readReceipt");

      return {
        isLoading: _.includes([1, 2], getMessageReadReceiptCount.networkStatus),
        isData: !_.isEmpty(node),
        readReceiptCount,
      };
    },
  }),
  withLoader
)(RecipientsModal);

const RecipientsModalContainer = props => {
  const { message, onCloseModal } = props;
  const { t } = useI18n(["common"]);

  const id = _.get(message, "id");

  return (
    <Modal onClose={onCloseModal} width="480px" height="520px" dsVersion="2.0">
      <Modal.Header>{t("common:read_status")}</Modal.Header>
      <Modal.Body style={modalBodyStyle}>
        <div className={classes.modalBody}>
          <RecipientsModalWithData messageId={id} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default RecipientsModalContainer;
